import cn from 'classnames';
import { hasElements, rm } from 'utils';
import {
  getProductDescription,
  getProductPdfs,
  getEfficiencyLabel,
  getIsTopTested,
  getEfficiencyLabel2020,
  getBundleProductLinks
} from 'views/ProductPage/selector';
import styles from '../styles.scss';
import { IconPdf } from 'components/Shared/Icon';
import Markdown from 'components/Shared/Markdown';
import { Block } from 'components/Shared/Layout';
import { ProductPdf } from 'models/ProductPdf';
import { TopTested } from 'components/Shared/Product/topTested';
import { useSelector } from 'react-redux';
import { ReactComponent } from 'models/ReactComponent';
import { Picture as PictureModel } from 'models/Picture';
import { BundleProductLink } from 'models/BundleProductLink';

const Pdf = ({ className, fileUrl, displayName }: ProductPdf & { className?: string }) => (
  <li>
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={fileUrl}
      className={cn(className, styles.productPdfLink)}
    >
      <IconPdf size="small" />
      {displayName}
    </a>
  </li>
);

interface Props {
  description?: ReactComponent;
  productPdfs: ProductPdf[];
  isTopTested: boolean;
  efficiencyLabel: ReactComponent[] | undefined;
  efficiencyLabel2020: PictureModel[] | undefined;
  pdfClassName?: string;
  bundleProductLinks?: BundleProductLink[];
}

export const _ProductDescription = ({
  description,
  productPdfs,
  isTopTested,
  efficiencyLabel,
  efficiencyLabel2020,
  pdfClassName,
  bundleProductLinks
}: Props) => (
  <Block className={styles.productDescription}>
    <Markdown className={styles.description} {...description} />
    {isTopTested && (
      <>
        <TopTested className={styles.topTested} />
        <h4>{rm('product_toptested_title')}</h4>
        <p>{rm('product_toptested_body')}</p>
      </>
    )}
    {hasElements(bundleProductLinks) && (
      <div className={styles.productBundleLinks}>
        <h4>{rm('product_bundle_savings_title')}</h4>
        <ul className={styles.productPdfLinks}>
          {bundleProductLinks.map(bundleProductLink => (
            <li key={bundleProductLink.url}>
              <a className={styles.productPdfLink} href={bundleProductLink.url}>
                {bundleProductLink.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    )}
    {hasElements(productPdfs) && (
      <>
        <h4>{rm('product_description_documents')}</h4>
        <ul className={styles.productPdfLinks}>
          {productPdfs.map(pdf => (
            <Pdf {...pdf} key={pdf.fileUrl} className={pdfClassName} />
          ))}
        </ul>
      </>
    )}
    {hasElements(efficiencyLabel) && (
      <ul className={styles.efficiencyLabel}>
        {efficiencyLabel.map((e, i) => (
          <Markdown key={i} {...e} />
        ))}
      </ul>
    )}
    {hasElements(efficiencyLabel2020) && (
      <ul className={styles.efficiencyLabel2020}>
        {efficiencyLabel2020.map((e, i) => (
          <li key={i}>
            <img src={e.src} />
          </li>
        ))}
      </ul>
    )}
  </Block>
);

export const ProductDescription = () => {
  const description = useSelector(getProductDescription);
  const productPdfs = useSelector(getProductPdfs);
  const isTopTested = useSelector(getIsTopTested);
  const efficiencyLabel = useSelector(getEfficiencyLabel);
  const efficiencyLabel2020 = useSelector(getEfficiencyLabel2020);
  const bundleProductLinks = useSelector(getBundleProductLinks);

  return (
    <_ProductDescription
      {...{
        description,
        productPdfs,
        isTopTested,
        efficiencyLabel,
        efficiencyLabel2020,
        bundleProductLinks
      }}
    />
  );
};
